/* News-index loop — the Act II migration field as a teaser.
   Ace Strategies house data-visualisation system.

   This is deliberately NOT the panel from the story page. It carries no
   tooltips, no table view, no focus layer and no controls, because a teaser
   that looks interactive but is not is worse than one that plainly is not. It
   loops, it counts, and the whole block is a link.

   It shares `data-v5.js` with the story and repeats that panel's allocation
   exactly — largest remainder within each 2025 cohort — and asserts the result
   against the poll's published baseline at runtime. If the two ever drift, the
   console says so rather than the page quietly showing different numbers from
   the article it links to. */
(function () {
  "use strict";

  const root = document.querySelector(".viz-loop");
  if (!root || !window.V5DATA) return;

  const D = window.V5DATA;
  const REDUCED = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  /* A device that will struggle with a rAF loop over a hundred nodes gets the
     same treatment as a stated motion preference: the finished 2026 field,
     drawn once. It is the state the piece resolves to anyway, so nothing is
     lost but the animation.

     The thresholds are deliberately low. Save-Data is an explicit request, and
     two cores or two gigabytes is a genuinely weak phone, not a mid-range one
     — a four-core cut would take the animation away from a large share of
     ordinary visitors to buy something the cycle budget below already buys.
     Both properties are missing on some browsers, which reads as "no reason to
     hold back". */
  const CONN = navigator.connection || {};
  const LOW_END = CONN.saveData === true
    || (navigator.hardwareConcurrency > 0 && navigator.hardwareConcurrency <= 2)
    || (navigator.deviceMemory > 0 && navigator.deviceMemory <= 2);
  const STATIC = REDUCED || LOW_END;
  const SVG_NS = "http://www.w3.org/2000/svg";
  const PARTY = {}; D.parties.forEach(p => PARTY[p.id] = p);
  const COLS = D.parties.map(p => p.id);

  const host = root.querySelector(".viz-loop__field");
  const boardEl = root.querySelector(".viz-loop__board");
  const boardSub = root.querySelector(".viz-loop__board-sub");
  const liveEl = root.querySelector(".viz-loop__live");
  const countEl = root.querySelector(".viz-loop__count");
  if (!host) return;

  function svg(tag, attrs) {
    const n = document.createElementNS(SVG_NS, tag);
    if (attrs) for (const k in attrs) if (attrs[k] !== null) n.setAttribute(k, attrs[k]);
    return n;
  }
  const rnd = (i, salt) => {
    const x = Math.sin((i + 1) * 12.9898 + salt * 78.233) * 43758.5453;
    return x - Math.floor(x);
  };

  /* ══════════ the departure board ══════════
     Both faces are the same length so the row never reflows mid-spin, and each
     cell cascades a little later than the one to its left, which is what makes
     a real board read left-to-right as it settles. */
  const FACES = {
    from: { board: "MAY 2025", sub: "2025 federal election" },
    to:   { board: "JUL 2026", sub: "DemosAU · Capital Brief poll" }
  };
  const GLYPHS = "ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789";
  /* The drop-in has to restart on every glyph, and the CSS-class version of
     that needs a forced reflow per glyph to flush the removal — about a
     hundred synchronous layouts per board turn. The Web Animations API
     restarts by being called again, so it needs none. `.is-turning` stays as
     the fallback path and keeps the keyframes as the single description of
     the motion. */
  const CAN_ANIMATE = typeof Element.prototype.animate === "function";
  const FLAP_FRAMES = [
    { transform: "translateY(-100%)", opacity: 0.35 },
    { opacity: 1, offset: 0.6 },
    { transform: "translateY(0)", opacity: 1 }
  ];
  const flaps = [];
  if (boardEl) {
    [...FACES.from.board].forEach(ch => {
      const cell = document.createElement("span");
      cell.className = "viz-loop__flap" + (ch === " " ? " viz-loop__flap--gap" : "");
      cell.setAttribute("aria-hidden", "true");
      const inner = document.createElement("span");
      inner.textContent = ch;
      cell.appendChild(inner);
      boardEl.appendChild(cell);
      flaps.push({ cell, inner, ch });
    });
  }
  let boardTimers = [];
  function clearBoard() { boardTimers.forEach(clearTimeout); boardTimers = []; }

  function setBoard(key, instant) {
    const face = FACES[key];
    if (liveEl) liveEl.textContent = face.sub;
    if (!flaps.length) { if (boardSub) boardSub.textContent = face.sub; return; }
    clearBoard();
    const target = face.board;
    let settle = 0;
    flaps.forEach((f, i) => {
      const want = target[i] || " ";
      if (instant || STATIC) { f.inner.textContent = want; f.ch = want; f.cell.classList.remove("is-turning"); return; }
      if (f.ch === want) return;
      /* spin a few glyphs before landing; cells further right run longer */
      const turns = 5 + Math.floor(rnd(i, 9) * 5) + i;
      settle = Math.max(settle, 40 + i * 55 + turns * 46);
      let n = 0;
      const tick = () => {
        n++;
        if (n >= turns) { f.inner.textContent = want; f.ch = want; f.cell.classList.remove("is-turning"); return; }
        f.inner.textContent = want === " " ? " " : GLYPHS[Math.floor(rnd(i * 31 + n, 3) * GLYPHS.length)];
        if (CAN_ANIMATE) {
          f.inner.animate(FLAP_FRAMES, { duration: 110, easing: "linear" });
        } else {
          f.cell.classList.add("is-turning");
          /* retrigger the drop-in on every glyph */
          void f.cell.offsetWidth;
        }
        boardTimers.push(setTimeout(tick, 46));
      };
      boardTimers.push(setTimeout(tick, 40 + i * 55));
    });
    /* the caption lands with the last flap — swapping it up front left it
       naming one date while the board still read the other */
    if (!boardSub) return;
    if (instant || STATIC || !settle) boardSub.textContent = face.sub;
    else boardTimers.push(setTimeout(() => boardSub.textContent = face.sub, settle));
  }

  /* ══════════ numbers that tumble rather than snap ══════════ */
  let scrambleTimers = [];
  function clearScramble() { scrambleTimers.forEach(clearTimeout); scrambleTimers = []; }
  function scrambleTo(setText, mark, from, to, ms, delay) {
    const steps = Math.max(4, Math.round(ms / 60));
    let n = 0;
    const tick = () => {
      n++;
      if (n >= steps) { mark && mark(false); setText(to); return; }
      mark && mark(true);
      /* wander between the two values rather than to random noise, so the
         digits read as counting back rather than as a broken readout */
      const lo = Math.min(from, to), hi = Math.max(from, to);
      setText(lo + Math.floor(rnd(n * 7 + from, 5) * (hi - lo + 1)));
      scrambleTimers.push(setTimeout(tick, 60));
    };
    scrambleTimers.push(setTimeout(tick, delay || 0));
  }

  /* ── allocate 100 circles to (from → to) pairs, largest remainder ── */
  const pairs = [];
  D.retention.forEach(src => COLS.forEach(t => {
    const pct = src.to[t] || 0;
    if (pct > 0) pairs.push({ from: src.id, to: t, pct });
  }));
  D.retention.forEach(src => {
    const mine = pairs.filter(p => p.from === src.id);
    mine.forEach(p => { p.exact = src.share * p.pct / 100; p.n = Math.floor(p.exact); });
    let left = src.share - mine.reduce((a, p) => a + p.n, 0);
    mine.slice().sort((a, b) => (b.exact - b.n) - (a.exact - a.n)).forEach(p => { if (left-- > 0) p.n++; });
  });

  const dots = [];
  pairs.forEach(p => { for (let i = 0; i < p.n; i++) dots.push({ from: p.from, to: p.to }); });
  dots.sort((a, b) => COLS.indexOf(a.from) - COLS.indexOf(b.from) || (a.from === a.to ? -1 : 1) - (b.from === b.to ? -1 : 1));

  const tally = key => { const t = {}; COLS.forEach(id => t[id] = 0); dots.forEach(d => t[d[key]]++); return t; };
  const T25 = tally("from"), T26 = tally("to");

  /* the piles must reproduce the poll's own election baseline, not drift on rounding */
  const drift = COLS.filter(id => T25[id] !== D.retention.find(r => r.id === id).share);
  if (drift.length || dots.length !== 100) {
    console.warn("[loop] allocation drifted from the published 2025 baseline:", drift, dots.length);
  }

  /* ── geometry: piles side by side, stacking into bands on narrow screens ── */
  const GAP = 19, R = 7.4;
  const maxPer = {}; COLS.forEach(id => maxPer[id] = Math.max(T25[id], T26[id]));

  function buildGeo() {
    const narrow = (host.clientWidth || 700) < 520;
    if (!narrow) {
      const PER = 5, PW = PER * GAP, PG = 30;
      const W = COLS.length * PW + (COLS.length - 1) * PG;
      const H = Math.ceil(Math.max(...COLS.map(id => maxPer[id])) / PER) * GAP + 62;
      const baseY = H - 34;
      const px = id => COLS.indexOf(id) * (PW + PG);
      return { narrow, W, H,
        slot: (id, j) => ({ x: px(id) + (j % PER) * GAP + GAP / 2, y: baseY - Math.floor(j / PER) * GAP - GAP / 2 }),
        name: id => ({ x: px(id) + PW / 2, y: baseY + 19, anchor: "middle" }),
        value: id => ({ x: px(id) + PW / 2, y: baseY + 33, anchor: "middle" }) };
    }
    /* 20 a row rather than the story panel's 10: this is a teaser sitting
       directly under the masthead, and at 10 the section ran to 1,350px on a
       phone — most of two viewports before the reader reaches the news. */
    const PER = 20, W = PER * GAP, LBL = 20, PAD = 12;
    const top = {}; let y = 0;
    COLS.forEach(id => { top[id] = y; y += LBL + Math.ceil(maxPer[id] / PER) * GAP + PAD; });
    return { narrow, W, H: y,
      slot: (id, j) => ({ x: (j % PER) * GAP + GAP / 2, y: top[id] + LBL + Math.floor(j / PER) * GAP + GAP / 2 }),
      name: id => ({ x: 0, y: top[id] + 11, anchor: "start" }),
      value: id => ({ x: W, y: top[id] + 11, anchor: "end" }) };
  }
  let GEO = buildGeo();

  const field = svg("svg", { viewBox: `0 0 ${GEO.W} ${GEO.H}`, class: "viz-loop__svg",
    role: "img", "aria-label":
      "One hundred circles, one for every percent of the 2025 federal vote, re-sorting into July 2026 voting intention. " +
      "The Coalition's pile falls from 32 to 21 as twelve of its voters join One Nation, whose pile rises from 6 to 29." });
  const labelsG = svg("g"), dotsG = svg("g");
  field.appendChild(labelsG); field.appendChild(dotsG);
  host.appendChild(field);

  const nodes = dots.map(() => { const c = svg("circle", { r: R, class: "viz-loop__dot" }); dotsG.appendChild(c); return c; });

  function layout(key) {
    const seen = {}; COLS.forEach(id => seen[id] = 0);
    return dots.map(d => { const id = d[key]; return { ...GEO.slot(id, seen[id]++), party: id }; });
  }

  /* kept so the running totals can be rewritten each frame without rebuilding */
  const valEls = {};
  function paintLabels(counts) {
    labelsG.textContent = "";
    COLS.forEach(id => {
      const nm = GEO.name(id), vl = GEO.value(id);
      const a = svg("text", { x: nm.x, y: nm.y, class: "viz-loop__name", "text-anchor": nm.anchor });
      a.textContent = PARTY[id].short;
      const b = svg("text", { x: vl.x, y: vl.y, class: "viz-loop__val", "text-anchor": vl.anchor });
      b.textContent = counts[id];
      valEls[id] = b;
      labelsG.appendChild(a); labelsG.appendChild(b);
    });
  }
  function writeCounts(counts) { COLS.forEach(id => { if (valEls[id]) valEls[id].textContent = counts[id]; }); }

  function place(key) {
    const pos = layout(key);
    nodes.forEach((c, i) => {
      c.setAttribute("cx", pos[i].x.toFixed(1));
      c.setAttribute("cy", pos[i].y.toFixed(1));
      c.setAttribute("r", R);
      c.setAttribute("fill", `var(--p-${pos[i].party})`);
    });
    paintLabels(key === "from" ? T25 : T26);
    if (countEl) countEl.textContent = (key === "from" ? T25 : T26).on;
  }

  /* ── the migration, with per-circle timing and a bowed path ── */
  let raf = null;
  function migrate(done) {
    const from = layout("from"), to = layout("to");
    const tw = nodes.map((c, i) => {
      const dx = to[i].x - from[i].x, dy = to[i].y - from[i].y, dist = Math.hypot(dx, dy);
      const travels = dist > 1;
      return { c, i, from: from[i], to: to[i], dist, travels,
        delay: travels ? 60 + rnd(i, 1) * 520 : 0,
        dur: travels ? 700 + rnd(i, 2) * 640 : 1,
        bow: travels ? (rnd(i, 3) - 0.5) * Math.min(dist * 0.4, 70) : 0,
        ease: 2.0 + rnd(i, 4) * 1.8 };
    });
    const total = Math.max(...tw.map(t => t.delay + t.dur));
    const t0 = performance.now();

    let switched = false;
    (function step(now) {
      const e = now - t0, done_p = Math.min(1, e / total);
      const live = {}; COLS.forEach(id => live[id] = 0);
      tw.forEach(t => {
        const p = Math.min(1, Math.max(0, (e - t.delay) / t.dur));
        const k = 1 - Math.pow(1 - p, t.ease);
        let x = t.from.x + (t.to.x - t.from.x) * k, y = t.from.y + (t.to.y - t.from.y) * k;
        let party = t.from.party;
        if (t.travels) {
          const nx = -(t.to.y - t.from.y) / (t.dist || 1), ny = (t.to.x - t.from.x) / (t.dist || 1);
          const arc = Math.sin(Math.PI * k) * t.bow;
          x += nx * arc; y += ny * arc;
          t.c.setAttribute("r", (R * (1 + Math.sin(Math.PI * k) * 0.2)).toFixed(2));
          /* a circle counts for its destination from the moment it takes that
             colour, so the pile totals and the field always agree */
          if (p > 0.45) { party = t.to.party; t.c.setAttribute("fill", `var(--p-${party})`); }
        }
        live[party]++;
        t.c.setAttribute("cx", x.toFixed(1)); t.c.setAttribute("cy", y.toFixed(1));
      });
      writeCounts(live);
      if (countEl) countEl.textContent = live.on;
      /* the board turns as the field commits, not after it has arrived */
      if (!switched && done_p > 0.30) { switched = true; setBoard("to"); }
      if (e < total) raf = requestAnimationFrame(step);
      else { raf = null; place("to"); done && done(); }
    })(t0);
  }

  /* ── the loop: hold, migrate, hold, dissolve, reset ──
     The reset fades out and back rather than reversing the migration: running
     it backwards would show voters returning to the Coalition, which is not
     what the poll found. */
  let timers = [], running = false, stopped = false;
  /* The loop plays a set number of times and then rests on the 2026 field
     rather than running for as long as the section is on screen. A perpetual
     animation is sustained main-thread work whatever else it does well, and
     sustained main-thread work is what INP measures — a tap landing mid-frame
     waits behind it. Scrolling the block away and back winds it up again, so
     a reader who wants to watch it twice still can. */
  const MAX_CYCLES = 3;
  let cyclesRun = 0, settled = false;
  const wait = (ms, fn) => timers.push(setTimeout(fn, ms));
  function clear() {
    timers.forEach(clearTimeout); timers = [];
    clearBoard(); clearScramble();
    nodes.forEach(c => c.style.transition = "");
    if (raf) { cancelAnimationFrame(raf); raf = null; }
  }

  /* Wind the totals back to the 2025 baseline before replaying. Running the
     migration in reverse would show voters returning to the Coalition, which is
     not what the poll found — but the counters can legitimately roll back,
     because they are a readout, not the electorate. */
  function rewind(done) {
    clearScramble();
    setBoard("from");
    COLS.forEach((id, i) => {
      const el = valEls[id];
      if (!el) return;
      scrambleTo(v => el.textContent = v,
        on => el.classList.toggle("is-scrambling", on),
        T26[id], T25[id], 620, i * 55);
    });
    if (countEl) scrambleTo(v => countEl.textContent = v, null, T26.on, T25.on, 700, 0);
    /* the circles slide home under the numbers, unremarked */
    const pos = layout("from");
    nodes.forEach((c, i) => {
      c.style.transition = `cx .7s cubic-bezier(.22,.9,.28,1) ${(i % 14) * 22}ms, cy .7s cubic-bezier(.22,.9,.28,1) ${(i % 14) * 22}ms, fill .5s ease`;
      c.setAttribute("cx", pos[i].x.toFixed(1));
      c.setAttribute("cy", pos[i].y.toFixed(1));
      c.setAttribute("fill", `var(--p-${pos[i].party})`);
    });
    wait(1150, () => { nodes.forEach(c => c.style.transition = ""); done && done(); });
  }

  function cycle(first) {
    if (stopped) return;
    running = true;
    if (first) { place("from"); setBoard("from", true); }
    wait(first ? 1100 : 260, () => {
      if (stopped) return;
      migrate(() => {
        if (stopped) return;
        /* Count the arrival, not the departure: the run that exhausts the
           budget still finishes, so the field always comes to rest on the
           2026 allocation rather than half way back to 2025. */
        if (++cyclesRun >= MAX_CYCLES) { settled = true; running = false; return; }
        wait(2900, () => {
          if (stopped) return;
          rewind(() => { if (!stopped) cycle(false); });
        });
      });
    });
  }
  /* Three independent reasons to be idle — off screen, tab in the background,
     or held under the pointer. A single boolean is not enough: an
     IntersectionObserver tick would clear a deliberate hover-pause, because
     observer callbacks land asynchronously after the pointer event. */
  const idle = { offscreen: true, hidden: false, held: false };
  function sync() {
    const shouldRun = !STATIC && !settled && !idle.offscreen && !idle.hidden && !idle.held;
    if (shouldRun && !running) { clear(); stopped = false; cycle(true); }
    else if (!shouldRun && running) { stopped = true; running = false; clear(); }
  }
  /* Leaving the viewport is the one thing that refills the budget. Hover and
     tab-switching pause and resume mid-run; they do not buy extra cycles. */
  function setIdle(reason, value) {
    if (reason === "offscreen" && value) { cyclesRun = 0; settled = false; }
    idle[reason] = value;
    sync();
  }

  /* only while it is on screen, and only while the tab is in front */
  if (STATIC) {
    place("to");
    setBoard("to", true);
    root.classList.add("is-static");
  } else {
    place("from");
    setBoard("from", true);
    if ("IntersectionObserver" in window) {
      new IntersectionObserver(es => es.forEach(e => setIdle("offscreen", !e.isIntersecting)),
        { threshold: 0.25 }).observe(root);
    } else { setIdle("offscreen", false); }
    document.addEventListener("visibilitychange", () => setIdle("hidden", document.hidden));
    /* let someone actually look at it */
    root.addEventListener("pointerenter", () => setIdle("held", true));
    root.addEventListener("pointerleave", () => setIdle("held", false));
    root.addEventListener("focusin", () => setIdle("held", true));
    root.addEventListener("focusout", () => setIdle("held", false));
  }

  let rz;
  window.addEventListener("resize", () => {
    clearTimeout(rz);
    rz = setTimeout(() => {
      const was = GEO.narrow;
      GEO = buildGeo();
      field.setAttribute("viewBox", `0 0 ${GEO.W} ${GEO.H}`);
      if (was !== GEO.narrow) field.classList.toggle("viz-loop__svg--stacked", GEO.narrow);
      /* A resize while the loop is at rest has to redraw the state the board
         is actually showing. Resting now means either "never animated" or
         "finished", and those are different fields. */
      if (!running) place(STATIC || settled ? "to" : "from");
    }, 200);
  });
  field.classList.toggle("viz-loop__svg--stacked", GEO.narrow);
})();
